import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';

import {BarSelection} from 'common/charts/visualization/conversion-column-viz';
import {ConversionStepBarType, NormalConversionStep} from 'common/charts/visualization/conversion-column-viz/util';
import {
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsResourceType,
  InsightsSeriesTree,
  MetricBehavior,
} from 'common/report/insights/util/types';
import {CountType} from 'common/report/funnels/models/types';
import {
  OTHER_PROP,
  OVERALL_PROP,
  OVERALL_PROP_NAME,
  TableToggleableMetrics,
} from 'common/report/funnels/models/constants';
import {getBehaviorShowClauses} from 'common/report/insights/models/utils';
import {getSegmentDisplayName} from 'common/report/funnels/util';
import {getSegmentPropertyType} from 'common/query-builder/query-block/segment-menu/types';
import {getSelectedSegments} from 'common/report/insights/util/segments';
import {getGroupByProperties} from 'common/query-builder/query-block/segment-menu/util';
import {Report} from 'common/report/insights/models/report';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {flattenSeriesToDepth, formatDateRange, getSortConfig} from 'common/report/insights/util';
import {getCalendarDate} from 'common/util/date';
import {reconcileComparisonSegments} from 'common/report/funnels/util/step-formatter';
import {ChartType, FUNNELS_MATH_TYPES, PropertyType, ResourceType, SavedLegend} from 'common/report/constants';
import {propertiesUtils} from 'common/report/util/properties';
import {renameEvent} from 'common/query-builder/cohorts-query-builder/util';
import {getDefaultSortConfig} from 'common/report/funnels/util/bar-chart';
import {MATH_TO_FUNNEL_STEPS_METRIC} from 'common/widgets/alerts/custom-alert/constants';
import {SegmentColumnSpec} from 'common/components/data-table/types';

import {StepChartData} from '../util';
import {FunnelStepBookmarkType, FunnelTableRow} from '../types';
import {getColumnGroups, getRows} from '../table-util';
import {StepChartBookmarkController} from './index';

export class MultimetricStepBookmarkController implements StepChartBookmarkController {
  type = FunnelStepBookmarkType.MULTIMETRIC;
  bookmark: InsightsBookmarkParams;
  _queryResults: InsightsQueryResults;
  _metricKeys: string[];

  constructor(bookmark: InsightsBookmarkParams, results: InsightsQueryResults) {
    this.bookmark = bookmark;
    this._queryResults = results;
  }

  set queryResults(results: InsightsQueryResults) {
    this._queryResults = results;
    this._metricKeys = null;
  }

  get queryResults(): InsightsQueryResults {
    return this._queryResults;
  }

  get metricKeys(): string[] {
    if (this._metricKeys) return this._metricKeys;

    let segmentationLevel = this.getBreakdownCount();
    let metrics = Object.values(this.queryResults.series)[0];
    while (segmentationLevel > 0) {
      const metricKey = Object.keys(metrics)[0];
      metrics = Object.values(metrics)[0];
      // Stop the loop if the key is "$overall" or "$other" because it will be followed by metrics
      if (metricKey === OVERALL_PROP || metricKey === OTHER_PROP) {
        break;
      }
      segmentationLevel -= 1;
    }
    const metricKeys = Object.keys(metrics[`count`]);
    // Store computed keys for later use
    this._metricKeys = metricKeys;
    return metricKeys;
  }

  public getBookmarkWithAppliedDefaults(bookmark: InsightsBookmarkParams) {
    return bookmark;
  }

  public getConversionSteps(): Array<NormalConversionStep<never, string>> {
    if (this.isEmptyReport()) {
      return null;
    }
    const normal = this.getBars(this.queryResults.series, ConversionStepBarType.Normal);

    if (this.queryResults.time_comparison) {
      const comparison = this.getBars(this.queryResults.time_comparison.series, ConversionStepBarType.Comparison);
      return normal.map((stepInfo, idx) => {
        const comparisonMap = new Map(comparison[idx].bars.map((bar) => [bar.extras, bar]));
        return {...stepInfo, bars: stepInfo.bars.flatMap((bar) => [bar, comparisonMap.get(bar.extras)])};
      });
    } else {
      return normal;
    }
  }

  public getStepLabel(behaviors: MetricBehavior[], index: number): {label: string; queryLabel: string} {
    const numberOfComparisons = this.getNumberOfEventComparisons();
    const metricKeys = this.metricKeys;
    let label: string;
    if (numberOfComparisons > 0) {
      label = metricKeys[index];
    } else {
      const isAnyOrder = behaviors.some((behavior) => behavior.funnelOrder === `any`);
      const behavior = behaviors[index];
      if (isAnyOrder) {
        label = `Step ${index + 1}`;
      } else {
        label = renameEvent(renameEvent(behavior.name));
      }
    }
    const queryLabel = metricKeys[index];

    return {label, queryLabel};
  }

  public getBars(series: InsightsSeriesTree, type: ConversionStepBarType): Array<NormalConversionStep<never, string>> {
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    const metrics = Object.values(series)[0];
    const bars = behaviors?.map((_, i) => {
      // TODO nix the numbered label generation for something more robust (like reading from meta field)
      const {label, queryLabel} = this.getStepLabel(behaviors, i);
      const breakdownCount = this.getBreakdownCount();
      let bars;

      if (breakdownCount === 0) {
        bars = [
          {
            displayName: OVERALL_PROP_NAME,
            extras: OVERALL_PROP,
            value: this._getMetric(metrics, queryLabel, this._getCountMetricName()),
            conversionRate: this._getMetric(metrics, queryLabel, `step_conv_ratio`),
            type,
          },
        ];
      } else {
        const flatKeys = flattenSeriesToDepth(metrics, breakdownCount - 1);
        bars = flatKeys.map(([keys, leaf]) => ({
          value: this._getMetric(leaf, queryLabel, this._getCountMetricName()),
          conversionRate: this._getMetric(leaf, queryLabel, `step_conv_ratio`),
          displayName: this.getSegmentLabelForPath(keys),
          extras: this.getSegmentLabelForPath(keys),
          type,
        }));
      }
      return {
        displayName: label,
        bars,
      };
    });
    return bars;
  }

  public getSelectedStepBars(): BarSelection[] {
    // TODO
    return [{stepIndex: 0, barIndex: 0, isDropoff: false}];
  }

  private getStandardFunnelSegments() {
    const series = this.queryResults?.series;
    return this._getFunnelSegments(series);
  }

  private getLiftFunnelSegments() {
    const series = this.queryResults.lift_comparison?.series;
    return this._getFunnelSegments(series);
  }

  private getTimeComparisonFunnelSegments() {
    const series = this.queryResults.time_comparison?.series;
    return this._getFunnelSegments(series);
  }

  private _getMetricsForSegment(segment: InsightsSeriesTree<any>, behaviors: MetricBehavior[], index: number) {
    const behavior = behaviors[index];
    const {label, queryLabel} = this.getStepLabel(behaviors, index);
    const getMetric = (metricName) => this._getMetric(segment, queryLabel, metricName);

    const pValue = getMetric(`p_value`);

    return {
      count: getMetric(this._getCountMetricName()),
      avg_time: getMetric(`avg_time`),
      avg_time_from_start: getMetric(`avg_time_from_start`),
      event: behavior.name,
      goal: behavior.name,
      step_label: label,
      overall_conv_ratio: getMetric(`overall_conv_ratio`),
      step_conv_ratio: getMetric(`step_conv_ratio`),
      index,
      ...(this.shouldShowStatSig() && isNumber(pValue?.p) ? {p_value: pValue} : {}),
    };
  }

  private _getFunnelSegments(series: InsightsSeriesTree) {
    if (!series) {
      return null;
    }
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    const metrics = Object.values(series)[0];
    const breakdownCount = this.getBreakdownCount();

    if (!breakdownCount) {
      return [
        {
          prop: OVERALL_PROP,
          propName: OVERALL_PROP_NAME,
          props: [
            {
              prop: OVERALL_PROP,
              propName: OVERALL_PROP_NAME,
            },
          ],
          steps: behaviors?.map((_, i) => {
            return this._getMetricsForSegment(metrics, behaviors, i);
          }),
        },
      ];
    } else {
      // segments should only contain valid segment values. filter $overall
      const uniqueSegments = flattenSeriesToDepth(metrics, breakdownCount - 1);
      // TODO color
      return uniqueSegments.map(([path, leaf]) => ({
        prop: this.getSegmentLabelForPath(path),
        propName: this.getSegmentLabelForPath(path),
        props: path.map((seg) => ({propName: seg, prop: seg})), // TODO renames?
        steps: behaviors?.map((_, i) => this._getMetricsForSegment(leaf, behaviors, i)),
      }));
    }
  }

  private getCountType(): CountType {
    const countType = getBehaviorShowClauses(this.bookmark.sections.show)[0]?.measurement.math;
    // Subset of count types for now since new metrics dont follow the same rules atm
    return this._isPropSum()
      ? CountType.PropertySum
      : countType === FUNNELS_MATH_TYPES.CONVERSION_RATE
      ? CountType.ConversionRate
      : CountType.Unique;
  }

  private getMetricLabel(): string {
    const series = this.queryResults?.series;
    const funnelMetricLabel = Object.keys(series)[0];
    return funnelMetricLabel;
  }

  private shouldShowStatSig() {
    return (
      this.bookmark.sections.group.length > 0 &&
      !!this.bookmark.displayOptions.funnelStepsSelectedTableColumns?.[TableToggleableMetrics.StatSig]
    );
  }

  public getStepChartData(): StepChartData {
    const savedLegend: SavedLegend[] = this.bookmark?.legend?.filteredBreakdownSegments;
    const formattedDateRange = formatDateRange(
      {
        fromDate: this.queryResults.date_range?.from_date,
        toDate: this.queryResults.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const formattedTimeComparisonDateRange = formatDateRange(
      {
        fromDate: this.queryResults.time_comparison?.date_range?.from_date,
        toDate: this.queryResults.time_comparison?.date_range?.to_date,
      },
      {isPartialDay: false, isMultiDay: true, includeYear: true},
    );
    const comparisonDateRange = this.queryResults.time_comparison
      ? {
          from: getCalendarDate(this.queryResults.time_comparison?.date_range?.from_date),
          to: getCalendarDate(this.queryResults.time_comparison?.date_range?.to_date),
        }
      : null;
    return {
      conversionSteps: this.getConversionSteps(),
      legendSegmentItems: this.getLegendSegmentItems(false), //SegmentItem[];
      detailedLegendSegmentItems: this.getLegendSegmentItems(true), // SegmentItem[];
      legendSegmentLabel: ``, // TODO
      savedLegend,
      mainDateRangeLabel: formattedDateRange,
      comparisonDateRangeLabel: this.queryResults.time_comparison && formattedTimeComparisonDateRange,
      comparisonDateRange,
      countType: this.getCountType(),
      funnelSegmentsForPopover: this.getStandardFunnelSegments(), //FunnelSegment[];
      funnelSegments: this.getStandardFunnelSegments(), // FunnelSegment[];
      funnelLiftSegments: this.getLiftFunnelSegments(), // null | FunnelSegment[];
      funnelCompareSegments: this.getTimeComparisonFunnelSegments(), // FunnelSegment[] | null;
      funnelMetricLabel: this.getMetricLabel(),
    };
  }

  public getSorting() {
    const segmentationLevel = this.getBreakdownCount();
    const sortConfig = getSortConfig({
      bookmarkSortConfig: this.bookmark.sorting,
      liftComparison: this.bookmark.liftComparison,
      headerCount: segmentationLevel,
    })[ChartType.Bar];
    return sortConfig ?? getDefaultSortConfig(segmentationLevel);
  }

  getSegmentLabelForPath(segments: string[]): string {
    return segments.join(` / `);
  }

  getLegendSegmentItems(detailedLabel: boolean) {
    // TODO re-use getSegmentLabelWithConversionRate? Or add lift comparison
    const metrics = Object.values(this.queryResults.series)[0];
    const breakdownCount = this.getBreakdownCount();

    if (breakdownCount === 0) {
      const overall = metrics[`overall_conv_ratio`];
      const conversionRate = Object.values(overall)[0][`all`];
      return [
        {
          id: OVERALL_PROP,
          label: `${OVERALL_PROP_NAME} • ${Math.round(conversionRate * 10000) / 100}%`,
        },
      ];
    } else {
      const uniqueSegments = flattenSeriesToDepth(metrics, breakdownCount - 1);
      let result = [];
      result = uniqueSegments
        .map(([path, leaf]) => {
          const key = this.getSegmentLabelForPath(path);
          const overallConversion = Object.values(leaf[`overall_conv_ratio`])[0][`all`];
          return {
            id: key,
            label: detailedLabel ? `${key} • ${Math.round(overallConversion * 10000) / 100}%` : key,
          };
        })
        .filter(({id}) => id !== OVERALL_PROP);
      return result;
    }
  }

  getSegmentColumnSpecs(bookmark: InsightsBookmarkParams): SegmentColumnSpec[] {
    let segmentColumns: SegmentColumnSpec[] = [];

    const behaviors = getBehaviorShowClauses(bookmark.sections.show || [])[0]?.behavior?.behaviors;
    behaviors.forEach((beh, idx) => {
      if (!!beh.behaviors && behaviors.length >= 2) {
        segmentColumns.push({
          title: `Step ` + (idx + 1) + ` Comparison`,
          segmentType: PropertyType.String,
        });
      }
    });
    const segments = getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses, false);
    segmentColumns = segmentColumns.concat(
      segments.map((segment) => ({
        title: getSegmentDisplayName(segment),
        segmentType: getSegmentPropertyType(segment),
      })),
    );

    return segmentColumns;
  }

  getBookmarkAsReport() {
    // Utility to get the bookmark in report form (which has some serialization done on it)
    return Report.fromBookmark({params: this.bookmark, type: BookmarkType.Insights, name: ``, description: ``});
  }

  public getDataTableInfo({stepChartData, tableWidth, onStatSigClick}) {
    // the "selected" color Map assigns "tbd" (an arbitrary invalid string) for all keys because the generated table row color is expected to be overridden by SegmentFilterController in <mp-segment-filter-data-table>
    // but getRows() is an older method (still used outside this widget) that expects a color string
    const selected = stepChartData.savedLegend?.length
      ? new Map<string, string>(stepChartData.savedLegend[0].selected.map((colorChoice) => [colorChoice.key, `tbd`]))
      : new Map();
    const liftSegmentSteps = this.getLiftFunnelSegments();
    const compareSegmentSteps = this.getTimeComparisonFunnelSegments();
    const segmentSteps = this.getStandardFunnelSegments();
    const rows: FunnelTableRow[] = getRows({
      selected,
      controlGroup: (this.bookmark.displayOptions.statSigControl ?? []).map((segment) => segment.prop) ?? [],
      shouldShowStatSig: this.shouldShowStatSig(),
      segmentSteps,
      shouldFilterForSelected: false,
      liftComparison: this.bookmark.liftComparison,
      liftSegmentSteps,
      compareSegmentSteps,
    });

    const selectedColumns = this.bookmark.displayOptions.funnelStepsSelectedTableColumns;
    const segments = getSelectedSegments(this.getBookmarkAsReport().sections.group.clauses, false);
    const segmentSpec = this.getSegmentColumnSpecs(this.bookmark);
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    const stepNames = behaviors.map((_, i) => this.getStepLabel(behaviors, i).label);
    const segmentTypes = getGroupByProperties(segments).map((prop) => prop && prop.propertyType);
    const columnGroups = getColumnGroups({
      segmentSpec,
      onStatSigClick,
      stepNames,
      segmentTypes,
      shouldShowStatSig: this.shouldShowStatSig(),
      selectedColumns,
      tableWidth,
      showStepNamesInGroupHeaders: true,
      liftType: this.bookmark.liftComparison?.type,
    });
    if (compareSegmentSteps && !liftSegmentSteps) {
      const comparisonRows = getRows({
        selected,
        controlGroup: null, // TODO event comparison stat sig?
        shouldShowStatSig: this.shouldShowStatSig(),
        segmentSteps: reconcileComparisonSegments({
          segments: segmentSteps,
          comparisonSegments: compareSegmentSteps,
          updatePropName: true,
        }),
        shouldFilterForSelected: false, // TODO download mode
      });
      return {rows, comparisonRows, columnGroups};
    }
    // TODO
    return {rows, columnGroups};
  }

  public getDataGroupId(): string {
    return this.bookmark.sections.globalDataGroupId;
  }

  public isEmptyReport(): boolean {
    // when swapping reports there is a brief period where the response is for the wrong chart
    // TOOD is there a better way to detect that or just not allow that?
    return !this.queryResults.series || Object.values(this.queryResults.series).length === 0;
  }

  public isEquivalent(bookmark: any, results: any) {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }

  public getPropSumLabel(): string {
    const prop = this._getPropSumProp();
    const resourceType = (prop.resourceType === InsightsResourceType.Formulas
      ? ResourceType.Formula
      : prop.resourceType) as ResourceType;
    return `sum of ${propertiesUtils.renameProperty(this._getPropSumProp().name, resourceType, prop)}`;
  }

  private getNumberOfEventComparisons(): number {
    const behaviors = getBehaviorShowClauses(this.bookmark?.sections?.show || [])[0]?.behavior?.behaviors;
    return behaviors.reduce((acc, beh) => {
      if (`behaviors` in beh) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }

  private getBreakdownCount() {
    return this.bookmark.sections.group.length + this.getNumberOfEventComparisons();
  }

  private _getPropSumProp() {
    return getBehaviorShowClauses(this.bookmark.sections.show)[0].measurement.property;
  }

  private _isPropSum() {
    return !!this._getPropSumProp();
  }
  private _getCountMetricName() {
    const valueKeyKey = getBehaviorShowClauses(this.bookmark.sections.show)[0]?.measurement?.math;

    // conversion rate special cased since we already have separate columns for it
    if (valueKeyKey in MATH_TO_FUNNEL_STEPS_METRIC && this.getCountType() !== CountType.ConversionRate) {
      return MATH_TO_FUNNEL_STEPS_METRIC[valueKeyKey];
    } else if (this._isPropSum()) {
      return `property_sum`;
    } else {
      return `count`;
    }
  }
  private _getMetric(segment, queryLabel, metricName) {
    return segment[metricName]?.[queryLabel]?.[`all`];
  }
}
